<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import ConfirmReprocessCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmReprocessCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const route = useRoute();
const router = useRouter();

const selectedCampaign = ref(null);
const initialDraft = ref(null);
const activeCampaignTab = ref('all');
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const confirmReprocessCampaignDialogRef = ref(null);
const currentUser = useMapGetter('getCurrentUser');
const canPermanentDeleteCampaigns = computed(
  () => currentUser.value?.type === 'SuperAdmin'
);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);
const isInitialCampaignLoad = computed(
  () => isFetchingCampaigns.value && !WhatsAppCampaigns.value?.length
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const campaignMatchesTab = (campaign, tab) => {
  if (tab === 'all') return true;
  if (tab === 'completed') return campaign.campaign_status === 'completed';
  if (tab === 'paused') {
    return (
      campaign.campaign_status === 'paused' ||
      (campaign.enabled === false && campaign.campaign_status !== 'completed')
    );
  }

  return campaign.enabled !== false && campaign.campaign_status === tab;
};

const campaignTabs = computed(() =>
  [
    ['all', t('CAMPAIGN.WHATSAPP.FILTERS.ALL')],
    ['active', t('CAMPAIGN.WHATSAPP.FILTERS.SCHEDULED')],
    ['processing', t('CAMPAIGN.WHATSAPP.FILTERS.PROCESSING')],
    ['paused', t('CAMPAIGN.WHATSAPP.FILTERS.PAUSED')],
    ['completed', t('CAMPAIGN.WHATSAPP.FILTERS.COMPLETED')],
  ].map(([key, label]) => ({
    key,
    label,
    count: (WhatsAppCampaigns.value || []).filter(campaign =>
      campaignMatchesTab(campaign, key)
    ).length,
  }))
);

const activeCampaignTabIndex = computed(() =>
  Math.max(
    0,
    campaignTabs.value.findIndex(tab => tab.key === activeCampaignTab.value)
  )
);

const filteredWhatsAppCampaigns = computed(() =>
  (WhatsAppCampaigns.value || []).filter(campaign =>
    campaignMatchesTab(campaign, activeCampaignTab.value)
  )
);

const handleTabChanged = tab => {
  activeCampaignTab.value = tab.key;
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value?.open(campaign);
};

const handleHardDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value?.open(campaign, true);
};

const handleEdit = campaign => {
  selectedCampaign.value = campaign;
  toggleWhatsAppCampaignDialog(true);
};

const handleCreate = () => {
  selectedCampaign.value = null;
  initialDraft.value = null;
  toggleWhatsAppCampaignDialog(true);
};

const handleOpenTemplates = () => {
  router.push({
    name: 'campaigns_whatsapp_templates',
    params: { accountId: route.params.accountId },
  });
};

const handleCloseCampaignDialog = () => {
  selectedCampaign.value = null;
  initialDraft.value = null;
  toggleWhatsAppCampaignDialog(false);
};

const handleReprocess = async campaign => {
  selectedCampaign.value = campaign;
  confirmReprocessCampaignDialogRef.value?.open(campaign);
};

const handlePause = async campaign => {
  await store.dispatch('campaigns/pause', campaign.id);
  useAlert('Campanha pausada.');
};

const handleResume = async campaign => {
  await store.dispatch('campaigns/resume', campaign.id);
  useAlert('Campanha retomada.');
};

const handleRefresh = () => {
  store.dispatch('campaigns/get', { silent: true });
};

onMounted(() => {
  const draftKey = `talkvia:lemit:campaign-draft:${route.params.accountId}`;
  try {
    const draft = JSON.parse(sessionStorage.getItem(draftKey) || 'null');
    if (!draft?.numbers?.length) return;

    initialDraft.value = draft;
    sessionStorage.removeItem(draftKey);
    toggleWhatsAppCampaignDialog(true);
  } catch {
    sessionStorage.removeItem(draftKey);
  }
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="handleCreate"
    @close="handleCloseCampaignDialog"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        :campaign="selectedCampaign"
        :initial-draft="initialDraft"
        @close="handleCloseCampaignDialog"
      />
    </template>
    <template #header-actions>
      <Button
        :label="t('CAMPAIGN.WHATSAPP.TEMPLATES.CREATE')"
        icon="i-lucide-layout-template"
        size="sm"
        @click="handleOpenTemplates"
      />
    </template>
    <div
      v-if="isInitialCampaignLoad"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="!hasNoWhatsAppCampaigns" class="flex flex-col gap-4">
      <div
        v-if="isFetchingCampaigns"
        class="flex items-center gap-2 text-xs text-n-slate-10"
        role="status"
      >
        <Spinner class="size-3.5" />
        {{ t('CAMPAIGN.WHATSAPP.FILTERS.UPDATING') }}
      </div>
      <nav
        class="overflow-x-auto pb-1"
        :aria-label="t('CAMPAIGN.WHATSAPP.FILTERS.ARIA_LABEL')"
      >
        <TabBar
          :tabs="campaignTabs"
          :initial-active-tab="activeCampaignTabIndex"
          @tab-changed="handleTabChanged"
        />
      </nav>

      <CampaignList
        v-if="filteredWhatsAppCampaigns.length"
        :campaigns="filteredWhatsAppCampaigns"
        :can-permanent-delete="canPermanentDeleteCampaigns"
        @edit="handleEdit"
        @delete="handleDelete"
        @hard-delete="handleHardDelete"
        @reprocess="handleReprocess"
        @pause="handlePause"
        @resume="handleResume"
        @refresh="handleRefresh"
      />
      <div
        v-else
        class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak px-6 py-12 text-center"
      >
        <span class="i-lucide-list-filter size-5 text-n-slate-10" />
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.FILTERS.EMPTY') }}
        </p>
      </div>
    </div>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmReprocessCampaignDialog ref="confirmReprocessCampaignDialogRef" />
  </CampaignLayout>
</template>
